import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useDebounce } from './useDebounce';
import { safeJoin, hasValue } from '../utils/CommonUtils';
import { getStoredTimezone, resolveTimezoneMode } from '../utils/timezoneStorage';

dayjs.extend(utc);

export const useUrlFilters = (filterConfig = {}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [filters, setFilters] = useState(() => getFiltersFromParams(searchParams, filterConfig));
  const debouncedFilters = useDebounce(filters, filterConfig.debounceDelay || 300);

  useEffect(() => {
    const urlParams = buildUrlParams(searchParams, debouncedFilters, filterConfig);
    const shouldNavigate = hasParamsChanged(searchParams, urlParams);
    
    if (shouldNavigate) {
      navigate(`?${urlParams.toString()}`, { replace: true });
    }
  }, [debouncedFilters, navigate, searchParams]);

  const handleFilterChange = useCallback((filterType, value) => {
    setFilters(prevFilters => ({ ...prevFilters, [filterType]: value }));
  }, []);

  const handleDateRangeChange = useCallback((dateRange) => {
    const [start, end] = dateRange || [null, null];

    const startDate = start && dayjs(start).isValid() ? dayjs(start) : null;
    const endDate = end && dayjs(end).isValid() ? dayjs(end) : null;

    const config = filterConfig.dateFields?.[0];
    if (!config) {
      return;
    }

    setFilters(prevFilters => ({
      ...prevFilters,
      [config.startField]: startDate,
      [config.endField]: endDate,
    }));
  }, [filterConfig]); 

  const clearFilters = useCallback((specificFields = null) => {
    const fieldsToClear = specificFields || Object.keys(filterConfig.fields || {});
    setFilters(prevFilters => {
      const cleared = { ...prevFilters };
      fieldsToClear.forEach(field => {
        cleared[field] = filterConfig.fields?.[field]?.defaultValue || null;
      });
      return cleared;
    });
  }, [filterConfig]);

  const clearAllUrlFiltersRef = useRef(null);
  useEffect(() => {
    clearAllUrlFiltersRef.current = () => {
      const dateConfig = filterConfig.dateFields?.[0];
      setFilters(() => {
        const cleared = {};
        Object.entries(filterConfig.fields || {}).forEach(([key, fieldConfig]) => {
          cleared[key] = fieldConfig.defaultValue;
        });
        if (dateConfig) {
          cleared[dateConfig.startField] = null;
          cleared[dateConfig.endField] = null;
        }
        return cleared;
      });
    };
  });

  useEffect(() => {
    const handleClearAll = () => clearAllUrlFiltersRef.current?.();
    window.addEventListener('clearAllFilters', handleClearAll);
    return () => window.removeEventListener('clearAllFilters', handleClearAll);
  }, []);

  return {
    filters,
    debouncedFilters,
    handleFilterChange,
    handleDateRangeChange,
    clearFilters,
    setFilters,
  };
};

const getFiltersFromParams = (searchParams, config) => {
  const initialFilters = {};
  const timezoneMode = getTimezoneMode(searchParams);
  
  const hasAnyUrlParams = Array.from(searchParams.keys()).length > 0;
  
  Object.entries(config.fields || {}).forEach(([fieldKey, fieldConfig]) => {
    const paramValue = searchParams.get(fieldConfig.urlParam);
    initialFilters[fieldKey] = parseParamValue(paramValue, fieldConfig);
  });

  (config.dateFields || []).forEach(dateConfig => {
    const startParam = searchParams.get(dateConfig.startParam);
    const endParam = searchParams.get(dateConfig.endParam);
    const emptyDatesFlag = searchParams.get('emptyDates') === 'true';
    
    if (emptyDatesFlag && dateConfig.allowEmpty) {
      initialFilters[dateConfig.startField] = null;
      initialFilters[dateConfig.endField] = null;
    } else if (startParam || endParam) {
      initialFilters[dateConfig.startField] = startParam 
        ? parseDateParam(startParam, timezoneMode)
        : null;
      initialFilters[dateConfig.endField] = endParam 
        ? parseDateParam(endParam, timezoneMode)
        : null;
    } else if (hasAnyUrlParams && dateConfig.allowEmpty) {
      initialFilters[dateConfig.startField] = null;
      initialFilters[dateConfig.endField] = null;
    } else {
      initialFilters[dateConfig.startField] = dateConfig.defaultStart;
      initialFilters[dateConfig.endField] = dateConfig.defaultEnd;
    }
  });

  return initialFilters;
};

const parseParamValue = (paramValue, fieldConfig) => {
  if (!paramValue) return fieldConfig.defaultValue || [];
  
  switch (fieldConfig.type) {
    case 'array':
      return fieldConfig.parse ? fieldConfig.parse(paramValue) : paramValue.split(',');
    case 'json':
      try {
        return JSON.parse(paramValue);
      } catch {
        return fieldConfig.defaultValue || [];
      }
    case 'date':
      return dayjs(paramValue);
    default:
      return fieldConfig.parse ? fieldConfig.parse(paramValue) : paramValue;
  }
};

const buildUrlParams = (searchParams, filters, config) => {
  const urlParams = new URLSearchParams(searchParams);
  const timezoneMode = getTimezoneMode(urlParams);

  if (timezoneMode === 'utc') {
    urlParams.set('timezone', 'utc');
  } else {
    urlParams.delete('timezone');
  }
  
  Object.entries(config.fields || {}).forEach(([fieldKey, fieldConfig]) => {
    const value = filters[fieldKey];
    if (hasValue(value)) {
      const paramValue = formatParamValue(value, fieldConfig);
      urlParams.set(fieldConfig.urlParam, paramValue);
    } else {
      urlParams.delete(fieldConfig.urlParam);
    }
  });

  (config.dateFields || []).forEach(dateConfig => {
    const startValue = filters[dateConfig.startField];
    const endValue = filters[dateConfig.endField];
    
    if (startValue && dayjs(startValue).isValid()) {
      urlParams.set(dateConfig.startParam, formatDateParam(startValue, timezoneMode));
      urlParams.delete('emptyDates'); 
    } else {
      urlParams.delete(dateConfig.startParam);
    }

    if (endValue && dayjs(endValue).isValid()) {
      urlParams.set(dateConfig.endParam, formatDateParam(endValue, timezoneMode));
      urlParams.delete('emptyDates'); 
    } else {
      urlParams.delete(dateConfig.endParam);
    }
    
    const startIsEmpty = !startValue || (dayjs.isDayjs(startValue) && !startValue.isValid()) || startValue === null;
    const endIsEmpty = !endValue || (dayjs.isDayjs(endValue) && !endValue.isValid()) || endValue === null;
    
    if (dateConfig.allowEmpty && startIsEmpty && endIsEmpty) {
      urlParams.set('emptyDates', 'true');
    } else {
      urlParams.delete('emptyDates');
    }
  });

  return urlParams;
};

const formatParamValue = (value, fieldConfig) => {
  switch (fieldConfig.type) {
    case 'array':
      return safeJoin(value);
    case 'json':
      return JSON.stringify(value);
    case 'date':
      return value.format();
    default:
      return fieldConfig.format ? fieldConfig.format(value) : String(value);
  }
};

const getTimezoneMode = (searchParams) => {
  return resolveTimezoneMode(searchParams, getStoredTimezone());
};

const parseDateParam = (paramValue, timezoneMode) => {
  if (timezoneMode !== 'utc') {
    return dayjs(paramValue);
  }

  const utcDate = dayjs.utc(paramValue);
  if (!utcDate.isValid()) {
    return dayjs(paramValue);
  }

  return dayjs(
    new Date(
      utcDate.year(),
      utcDate.month(),
      utcDate.date(),
      utcDate.hour(),
      utcDate.minute(),
      utcDate.second(),
      utcDate.millisecond()
    )
  );
};

const formatDateParam = (value, timezoneMode) => {
  if (timezoneMode !== 'utc') {
    return value.format();
  }

  const date = dayjs(value).toDate();
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    )
  ).toISOString();
};

const hasParamsChanged = (currentParams, newParams) => {
  const newKeys = Array.from(newParams.keys());
  const currentKeys = Array.from(currentParams.keys());

  if (newKeys.length !== currentKeys.length) {
    return true;
  }

  for (const [key, value] of newParams) {
    if (currentParams.get(key) !== value) {
      return true;
    }
  }
  
  return false;
};


export const FilterConfigTypes = {
  TEXT: 'text',
  ARRAY: 'array',
  JSON: 'json',
  DATE: 'date'
};

export const createFieldConfig = (name, options = {}) => ({
  type: options.type || FilterConfigTypes.TEXT,
  urlParam: options.urlParam || name,
  defaultValue: options.defaultValue || '',
  parse: options.parse,
  format: options.format
});

export const createDateConfig = (startField, endField, options = {}) => {
  const defaultStart = options.defaultStart !== undefined 
    ? options.defaultStart 
    : dayjs().startOf('day');
  
  const defaultEnd = options.defaultEnd !== undefined
    ? options.defaultEnd
    : dayjs().endOf('day');

  return {
    startField,
    endField,
    startParam: options.startParam || `${startField}`,
    endParam: options.endParam || `${endField}`,
    defaultStart,
    defaultEnd,
    allowEmpty: options.allowEmpty || false,
  };
};
